import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function ProfessionalMapReviews({ cardId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ results: [], total: 0 });
  const [text, setText] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState({});
  const [reportId, setReportId] = useState(null);
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [page, setPage] = useState(0);
  const [isOwner, setIsOwner] = useState(false);
  const load = useCallback(async () => {
    if (!cardId) return;
    try {
      const result = await api.get("/professional-map/profiles/" + encodeURIComponent(cardId) + "/reviews", { params: { skip: page * 10, limit: 10 } });
      setData(result.data);
    } catch (_) { setError("Не удалось загрузить отзывы"); }
  }, [cardId, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let live = true;
    setIsOwner(false);
    if (user && cardId) {
      api.get("/professional-map/profiles/" + encodeURIComponent(cardId) + "/reviews/owner-access")
        .then(({ data }) => { if (live) setIsOwner(data.is_owner === true); })
        .catch(() => { if (live) setIsOwner(false); });
    }
    return () => { live = false; };
  }, [user, cardId]);
  async function send(event) {
    event.preventDefault();
    if (!user || !consent || busy) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api.post("/professional-map/profiles/" + encodeURIComponent(cardId) + "/reviews", { text, publication_consent: true });
      setText(""); setConsent(false);
      setMessage(result.data.state === "pending" ? "Отзыв отправлен на проверку" : "Отзыв опубликован");
      setPage(0); await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  }
  async function respond(id) {
    if (busy || !reply[id]?.trim()) return;
    setBusy(true); setError("");
    try {
      await api.post("/professional-map/profiles/" + encodeURIComponent(cardId) + "/reviews/" + encodeURIComponent(id) + "/reply", { text: reply[id] });
      setReply((old) => ({ ...old, [id]: "" })); setMessage("Ответ отправлен"); await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  }
  async function report(event) {
    event.preventDefault();
    if (!user || !reportId || busy) return;
    setBusy(true); setError("");
    try {
      await api.post("/professional-map/profiles/" + encodeURIComponent(cardId) + "/reviews/" + encodeURIComponent(reportId) + "/report", { reason, details });
      setReportId(null); setDetails(""); setMessage("Жалоба передана модератору");
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  }
  return <section className="mt-6 border-t border-[#826751]/15 pt-5" data-testid="pm-profile-reviews">
    <h2 className="font-serif text-xl">Отзывы о специалисте</h2>
    <p className="mt-2 text-sm text-[#715B4A]">Отзывы пользователей. Без рейтингов и звёзд.</p>
    {error && <p role="alert" className="mt-3 text-sm">{error}</p>}
    {message && <p role="status" className="mt-3 text-sm">{message}</p>}
    <div className="mt-4 space-y-3">
      {data.results.map((item) => <article key={item.id} className="rounded-xl border border-[#826751]/15 p-4">
        <p className="text-xs text-[#806B5C]">{item.author_name} · {new Date(item.created_at).toLocaleDateString("ru-RU")}</p>
        <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6">{item.text}</p>
        {item.owner_reply && <div className="mt-3 rounded-lg bg-[#F8F2E9] p-3">
          <p className="text-xs font-semibold">Ответ специалиста</p><p className="mt-1 whitespace-pre-wrap break-words text-sm">{item.owner_reply}</p>
        </div>}
        {user && <div className="mt-3 flex flex-wrap gap-3">
          <button type="button" onClick={() => { setReportId(item.id); setError(""); }} className="min-h-11 text-xs underline underline-offset-4">Сообщить о нарушении</button>
        </div>}
        {isOwner && !item.owner_reply && <details className="mt-2">
          <summary className="cursor-pointer text-xs underline underline-offset-4">Ответить как владелец страницы</summary>
          <div className="mt-2 space-y-2"><textarea rows={2} maxLength={1200} value={reply[item.id] || ""} onChange={(e) => setReply((old) => ({ ...old, [item.id]: e.target.value }))} className="w-full rounded-lg border p-2 text-sm" placeholder="Ответ специалиста (доступно только владельцу)"/>
            <button type="button" disabled={busy || !reply[item.id]?.trim()} onClick={() => respond(item.id)} className="min-h-11 rounded-lg border px-3 text-sm disabled:opacity-40">Отправить ответ</button>
          </div>
        </details>}
      </article>)}
      {data.total === 0 && <p className="text-sm text-[#715B4A]">Отзывов пока нет.</p>}
    </div>
    {data.total > 10 && <div className="mt-4 flex items-center gap-3">
      <button type="button" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="min-h-11 text-sm underline disabled:opacity-30">Назад</button>
      <span className="text-xs">{page + 1} / {Math.ceil(data.total / 10)}</span>
      <button type="button" disabled={(page + 1) * 10 >= data.total} onClick={() => setPage((p) => p + 1)} className="min-h-11 text-sm underline disabled:opacity-30">Далее</button>
    </div>}
    {user && !isOwner ? <form onSubmit={send} className="mt-5 space-y-3 rounded-xl bg-[#FFFCF8] p-4">
      <h3 className="font-serif text-lg">Оставить отзыв</h3>
      <textarea required minLength={10} maxLength={1800} rows={3} value={text} onChange={e => setText(e.target.value)} className="w-full rounded-lg border p-3 text-sm" placeholder="Поделись опытом сотрудничества. Не указывай медицинские сведения."/>
      <label className="flex gap-2 text-xs"><input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}/> Согласен на публичное размещение отзыва</label>
      <button disabled={busy || !consent || text.trim().length < 10} className="min-h-11 rounded-lg bg-[#654A39] px-4 text-sm text-white disabled:opacity-40">Опубликовать отзыв</button>
    </form> : !user ? <p className="mt-4 text-sm">Чтобы оставить отзыв или пожаловаться, войди в аккаунт.</p> : null}
    {reportId && <form onSubmit={report} className="mt-4 space-y-3 rounded-xl border p-4">
      <h3 className="font-serif text-lg">Сообщить о нарушении</h3>
      <select value={reason} onChange={e => setReason(e.target.value)} className="w-full rounded-lg border p-3 text-sm">
        <option value="spam">Спам</option><option value="impersonation">Выдача себя за другого</option>
        <option value="abuse">Оскорбления</option><option value="false_information">Недостоверная информация</option>
        <option value="privacy">Нарушение приватности</option><option value="other">Другое</option>
      </select>
      <textarea maxLength={500} rows={2} value={details} onChange={e => setDetails(e.target.value)} placeholder="Дополнительные сведения (необязательно)" className="w-full rounded-lg border p-3 text-sm"/>
      <div className="flex gap-3"><button disabled={busy} className="min-h-11 rounded-lg border px-3">Отправить жалобу</button><button type="button" onClick={() => setReportId(null)}>Отменить</button></div>
    </form>}
  </section>;
}
